/**
 * arabicGrammar.js
 *
 * Two layers of Quranic word grammar enrichment:
 *
 * 1. parseMorphology — converts raw morphology_summary tags into structured
 *    Arabic grammar labels (النوع / الصيغة / العدد / الجنس / الحالة / الجذر).
 *
 * 2. getCorrectedCase — overrides corpus case tags when the actual word
 *    ending makes the i'rāb unambiguous (e.g. ون → مرفوع).
 *
 * Neither function modifies the original corpus data.
 */

// ── Buckwalter → Arabic consonant map ────────────────────────────────────────
const BW = {
  "'": "ء", "|": "ا", ">": "أ", "&": "ؤ", "<": "إ", "}": "ئ",
  A: "ا", b: "ب", t: "ت", v: "ث", j: "ج", H: "ح", x: "خ",
  d: "د", "*": "ذ", r: "ر", z: "ز", s: "س", $: "ش", S: "ص",
  D: "ض", T: "ط", Z: "ظ", E: "ع", g: "غ", f: "ف", q: "ق",
  k: "ك", l: "ل", m: "م", n: "ن", h: "ه", w: "و", y: "ي",
};

/** Convert a Buckwalter-encoded root string to Arabic letters separated by spaces. */
export function buckwalterToArabic(bw) {
  return bw
    .split("")
    .map((c) => BW[c] ?? "")
    .filter(Boolean)
    .join(" ");
}

/** Strip Arabic diacritics (harakat, shadda, sukun, tatweel). */
function stripDiacritics(text) {
  return text.replace(/[\u064B-\u065F\u0640]/g, "");
}

// ── parseMorphology ───────────────────────────────────────────────────────────

/**
 * parseMorphology(morphology_summary)
 *
 * Splits a pipe-delimited morphology_summary string and maps the tags to
 * readable Arabic grammar labels.
 *
 * Example input:  "STEM|POS:N|ACT|PCPL|(V)|LEM:mutawak~iluwn|ROOT:wkl|MP|ACC"
 * Example output: { type_ar: "اسم", form_ar: "اسم فاعل", number_ar: "جمع",
 *                   gender_ar: "مذكر", case_ar: "منصوب", root_ar: "و ك ل" }
 *
 * Unknown or missing tags are silently omitted (field = null).
 * Designed to be extended: add new tag mappings without breaking existing ones.
 *
 * @param {string} morphology_summary
 * @returns {{ type_ar, form_ar, number_ar, gender_ar, case_ar, root_ar } | null}
 */
export function parseMorphology(morphology_summary) {
  if (!morphology_summary) return null;

  const tags = new Set(morphology_summary.split("|"));

  // ── Part of Speech ──────────────────────────────────────────────────────────
  let type_ar = null;
  for (const tag of tags) {
    if (tag === "POS:N")    { type_ar = "اسم";  break; }
    if (tag === "POS:V")    { type_ar = "فعل";  break; }
    if (tag === "POS:P")    { type_ar = "حرف";  break; }
    if (tag === "POS:PRON") { type_ar = "ضمير"; break; }
    if (tag === "POS:ADJ")  { type_ar = "صفة";  break; }
  }

  // ── Derived Form ────────────────────────────────────────────────────────────
  let form_ar = null;
  if (tags.has("PERF"))                        form_ar = "فعل ماضٍ";
  else if (tags.has("IMPF"))                   form_ar = "فعل مضارع";
  else if (tags.has("IMPV"))                   form_ar = "فعل أمر";
  else if (tags.has("ACT") && tags.has("PCPL")) form_ar = "اسم فاعل";
  else if (tags.has("PASS") && tags.has("PCPL")) form_ar = "اسم مفعول";
  else if (tags.has("VN"))                     form_ar = "مصدر";
  else if (tags.has("PN"))                     form_ar = "علم";

  // ── Number ──────────────────────────────────────────────────────────────────
  let number_ar = null;
  if (tags.has("MP") || tags.has("FP")) number_ar = "جمع";
  else if (tags.has("DU")) number_ar = "مثنى";
  else if (tags.has("MS") || tags.has("FS")) number_ar = "مفرد";

  // ── Gender ──────────────────────────────────────────────────────────────────
  let gender_ar = null;
  if (tags.has("MP") || tags.has("MS")) gender_ar = "مذكر";
  else if (tags.has("FP") || tags.has("FS")) gender_ar = "مؤنث";

  // ── Case (raw corpus) ───────────────────────────────────────────────────────
  let case_ar = null;
  if (tags.has("NOM")) case_ar = "مرفوع";
  else if (tags.has("ACC")) case_ar = "منصوب";
  else if (tags.has("GEN")) case_ar = "مجرور";

  // ── Root (Buckwalter → Arabic) ───────────────────────────────────────────────
  let root_ar = null;
  for (const tag of tags) {
    if (tag.startsWith("ROOT:")) {
      const converted = buckwalterToArabic(tag.slice(5));
      if (converted) root_ar = converted;
      break;
    }
  }

  const hasAny = type_ar || form_ar || number_ar || gender_ar || case_ar || root_ar;
  if (!hasAny) return null;

  return { type_ar, form_ar, number_ar, gender_ar, case_ar, root_ar };
}

/** Build a case result sourced directly from corpus morphology_summary. */
function buildFallback(morphology_summary) {
  if (!morphology_summary) return null;
  if (morphology_summary.includes("NOM"))
    return { case: "NOM", case_label_ar: "مرفوع", case_source: "corpus", case_confidence: "medium" };
  if (morphology_summary.includes("ACC"))
    return { case: "ACC", case_label_ar: "منصوب", case_source: "corpus", case_confidence: "medium" };
  if (morphology_summary.includes("GEN"))
    return { case: "GEN", case_label_ar: "مجرور", case_source: "corpus", case_confidence: "medium" };
  return null;
}

/**
 * getCorrectedCase(wordText, metadata)
 *
 * Derives the grammatical case for a Quranic word, correcting corpus tagging
 * errors when the word ending makes the case unambiguous.
 *
 * @param {string} wordText  Arabic word text with harakat (from Quran API)
 * @param {object} metadata  QuranWordMetadata record
 * @returns {{ case, case_label_ar, case_source, case_confidence } | null}
 *
 * case_source:
 *   "derived"  — corrected from word ending; takes precedence over corpus tag
 *   "corpus"   — taken directly from morphology_summary
 *
 * case_confidence:
 *   "high"   — unambiguous from word ending (e.g. ون → NOM)
 *   "medium" — probable but context-dependent (e.g. ين, corpus tag)
 */
export function getCorrectedCase(wordText, metadata) {
  if (!wordText || !metadata) return null;

  const { grammar_category, morphology_summary, word_type } = metadata;

  // Only nouns, adjectives, and participles carry grammatical case
  const correctable = ["noun", "adjective", "participle"].includes(grammar_category);
  if (!correctable) return buildFallback(morphology_summary);

  const norm = stripDiacritics(wordText);

  // ── Masculine Sound Plural (MP) correction ─────────────────────────────────
  // Triggered when the morphology flags MP, or when a noun/adjective ends with
  // ون (NOM) or ين (ACC/GEN) — the most reliable case signal in Arabic script.
  const isMP =
    (morphology_summary && morphology_summary.includes("MP")) ||
    ((grammar_category === "noun" || word_type === "noun") &&
      (norm.endsWith("ون") || norm.endsWith("ين")));

  if (isMP) {
    if (norm.endsWith("ون")) {
      return {
        case: "NOM",
        case_label_ar: "مرفوع",
        case_source: "derived",
        case_confidence: "high",
      };
    }
    if (norm.endsWith("ين")) {
      return {
        case: "ACC_GEN",
        case_label_ar: "منصوب / مجرور",
        case_source: "derived",
        case_confidence: "medium",
      };
    }
  }

  // ── Fallback: corpus morphology ─────────────────────────────────────────────
  return buildFallback(morphology_summary);
}
